/**
 * AdminAlertsTab — Iter82 (full redesign).
 *
 * Contract with the parent (AdminPanelPage):
 *   • The parent still fetches an unfiltered baseline via
 *     `adminListAlerts()` and passes `alerts / alertsHasMore / onLoadMore`.
 *   • This tab owns a compact toolbar + a collapsible filter panel.
 *     Whenever any filter, search or sort is active, the tab owns its
 *     own paginated fetch and IGNORES the parent baseline. Reset →
 *     falls back to the parent list (no regression).
 *
 * Server-side filtering exclusively (§19). Debounced search (§13).
 * Filters persist across navigation via sessionStorage.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Siren, MapPin, Flag, CheckCircle2, XCircle, BellRing } from "lucide-react";
import { EMERGENCY_FR } from "../../i18n.jsx";
import { EMERGENCY_META } from "../EmergencyTypeGrid";
import { AlertStatusControl } from "../AlertStatusControl";
import { mediaUrl, adminListAlerts, adminConfigFilters } from "../../lib/api";
import { MedicalProfileView } from "../MedicalProfileView";
import { adminFmtDate, AdminSectionHeading } from "./AdminPanelShared";
import { AlertsToolbar } from "./AlertsToolbar";
import { AlertsFilterPanel } from "./AlertsFilterPanel";
import { buildTimeParams } from "../../lib/timeFilter";

const STORAGE_KEY = "rr_admin_alerts_state_v2";

/** Default draft/applied state — everything empty (no filter). */
const emptyDraft = {
  preset: null,
  customSince: "",
  customUntil: "",
  statuses: [],
  types: [],
  assignments: [],
  hasEvidence: null,
  evidenceKinds: [],
  hasAmendment: null,
  advOpen: false,
};

/** Count the number of *individual selected values* (§2 spec). */
const countActive = (s) => {
  let n = 0;
  if (s.preset) n += 1;
  n += s.statuses.length;
  n += s.types.length;
  n += s.assignments.length;
  if (s.hasEvidence !== null) n += 1;
  n += s.evidenceKinds.length;
  if (s.hasAmendment !== null) n += 1;
  return n;
};

/** Serialise `applied` state to the /admin/alerts query params. */
const toQuery = (applied, search, sort) => {
  const p = {};
  if (applied.preset) {
    Object.assign(p, buildTimeParams(applied.preset, applied.customSince, applied.customUntil));
  }
  if (applied.statuses.length) p.status_filter = applied.statuses.join(",");
  if (applied.types.length) p.type_filter = applied.types.join(",");
  if (applied.assignments.length) p.assignment_filter = applied.assignments.join(",");
  if (applied.hasEvidence !== null) p.has_evidence = applied.hasEvidence ? "true" : "false";
  if (applied.evidenceKinds.length) p.evidence_kind = applied.evidenceKinds.join(",");
  if (applied.hasAmendment !== null) p.has_amendment = applied.hasAmendment ? "true" : "false";
  if (search && search.trim().length >= 2) p.search = search.trim();
  if (sort && sort !== "newest") p.sort = sort;
  return p;
};

const loadState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) { return null; }
};

const persistState = (s) => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_e) { /* noop */ }
};

export const AdminAlertsTab = ({
  alerts: parentAlerts,
  alertsHasMore: parentHasMore,
  unacked,
  onAckAlert,
  onMarkFalse,
  onLocate,
  onLoadMore: parentOnLoadMore,
  loadingMore,
  onStatusChanged,
}) => {
  const persisted = loadState();

  // Draft = what's in the panel (unsaved). Applied = what the server is showing.
  const [draft, setDraft] = useState(persisted?.applied || emptyDraft);
  const [applied, setApplied] = useState(persisted?.applied || emptyDraft);
  const [search, setSearch] = useState(persisted?.search || "");
  const [sort, setSort] = useState(persisted?.sort || "newest");
  const [panelOpen, setPanelOpen] = useState(false);

  // Config feed (agencies + emergency types) — cache in session
  const [config, setConfig] = useState({ agencies: [], emergency_types: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminConfigFilters();
        if (!cancelled) setConfig(d);
      } catch (_e) { /* keep empty defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Debounced search — kicks a fetch after 350 ms of no typing ─────
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const activeCount = useMemo(() => countActive(applied), [applied]);
  const hasAnyFilter = useMemo(() => {
    return (
      activeCount > 0 ||
      (debouncedSearch && debouncedSearch.trim().length >= 2) ||
      (sort && sort !== "newest")
    );
  }, [activeCount, debouncedSearch, sort]);

  // Persist state so navigation to an alert + back preserves it (§20).
  useEffect(() => {
    persistState({ applied, search, sort });
  }, [applied, search, sort]);

  // ── Server fetch (only when any filter/search/sort is active) ──────
  const [filteredList, setFilteredList] = useState({
    items: [], next_cursor: null, has_more: false, total_matching: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMoreFiltered, setLoadingMoreFiltered] = useState(false);
  const inflightRef = useRef(0);

  const fetchFiltered = useCallback(async ({ cursor = null } = {}) => {
    if (!hasAnyFilter) return;
    const params = { limit: 50, ...toQuery(applied, debouncedSearch, sort) };
    if (cursor) params.cursor = cursor;

    const reqId = ++inflightRef.current;
    if (cursor) setLoadingMoreFiltered(true);
    else setLoading(true);
    try {
      const d = await adminListAlerts(params);
      // Ignore obsolete responses (§19 — cancel/disregard stale).
      if (reqId !== inflightRef.current) return;
      setFilteredList((prev) => ({
        items: cursor ? [...prev.items, ...(d.items || [])] : (d.items || []),
        next_cursor: d.next_cursor || null,
        has_more: !!d.has_more,
        total_matching: typeof d.total_matching === "number" ? d.total_matching : (d.items || []).length,
      }));
    } catch (_e) {
      /* keep prior list to avoid blanking the operator */
    } finally {
      if (reqId === inflightRef.current) {
        setLoading(false);
        setLoadingMoreFiltered(false);
      }
    }
  }, [applied, debouncedSearch, sort, hasAnyFilter]);

  // Refetch whenever applied/search/sort changes.
  useEffect(() => {
    if (hasAnyFilter) fetchFiltered();
  }, [fetchFiltered, hasAnyFilter]);

  const listedAlerts = hasAnyFilter ? filteredList.items : (parentAlerts || []);
  const hasMore = hasAnyFilter ? filteredList.has_more : parentHasMore;
  const onLoadMore = hasAnyFilter
    ? () => fetchFiltered({ cursor: filteredList.next_cursor })
    : parentOnLoadMore;
  const loadingMoreEff = hasAnyFilter ? loadingMoreFiltered : loadingMore;
  const totalMatching = hasAnyFilter ? filteredList.total_matching : (parentAlerts?.length || 0);

  // Deep-link scroll (kept from Iter80).
  useEffect(() => {
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return;
    const id = setTimeout(() => {
      const el = document.querySelector(`[data-testid="admin-alert-${hash}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-emerald-400/70");
      setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400/70"), 2400);
    }, 120);
    return () => clearTimeout(id);
  }, [listedAlerts]);

  // ── Actions ────────────────────────────────────────────────────────
  const handleApply = () => {
    setApplied(draft);
    setPanelOpen(false);
  };
  const handleReset = () => {
    setDraft(emptyDraft);
    setApplied(emptyDraft);
    // Search stays (§15 — reset filters ≠ clear search).
  };

  // ── Rendering helpers ─────────────────────────────────────────────
  const summaryLine = useMemo(() => {
    const totalTxt = `${totalMatching} alertes trouvées`;
    if (!hasAnyFilter) return `Toutes les alertes · ${totalTxt}`;
    const filterTxt = activeCount === 0
      ? "Recherche active"
      : `${activeCount} filtre${activeCount > 1 ? "s" : ""} actif${activeCount > 1 ? "s" : ""}`;
    return `${filterTxt} · ${totalTxt}`;
  }, [activeCount, hasAnyFilter, totalMatching]);

  const fmt = adminFmtDate;

  return (
    <div className="px-3 sm:px-5 py-4 pb-8 space-y-3" data-testid="admin-alerts-list">
      {/* ── Toolbar ─────────────────────────────────────────────────── */}
      <AlertsToolbar
        search={search}
        onSearchChange={setSearch}
        onSearchClear={() => setSearch("")}
        activeFilterCount={activeCount}
        onOpenFilters={() => { setDraft(applied); setPanelOpen(true); }}
        sort={sort}
        onSortChange={setSort}
        totalMatching={totalMatching}
        loading={loading}
      />

      {/* Active filter summary (§16) */}
      <div className="text-[10px] font-mono text-zinc-500" data-testid="alerts-summary-line">
        {summaryLine}
      </div>

      {/* ── Filter panel (bottom sheet mobile / popover desktop) ────── */}
      {panelOpen && (
        <>
          <div
            aria-hidden
            className="fixed inset-0 bg-black/60 z-[500]"
            onClick={() => setPanelOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Filtres d'alertes"
            data-testid="alerts-filter-modal"
            className={
              /* Mobile: full-width bottom sheet · Desktop: centred sheet with max-w */
              "fixed z-[510] bg-[#0f1318] border border-zinc-700 shadow-2xl overflow-y-auto " +
              "inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl " +
              "sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 " +
              "sm:w-[560px] sm:max-w-[95vw] sm:max-h-[85vh] sm:rounded-md sm:border-zinc-700"
            }
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
          >
            <div className="sticky top-0 z-10 bg-[#0f1318] px-3 py-2 border-b border-zinc-800 flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-200">Filtres</span>
              <span className="text-[10px] font-mono text-zinc-500 ml-1">
                {activeCount} sélection{activeCount > 1 ? "s" : ""}
              </span>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                data-testid="alerts-close-filters"
                aria-label="Fermer le panneau"
                className="ml-auto text-zinc-400 hover:text-zinc-100 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] border border-zinc-700 hover:border-zinc-500"
              >
                Fermer
              </button>
            </div>
            <AlertsFilterPanel
              draft={draft}
              onChange={setDraft}
              onApply={handleApply}
              onReset={handleReset}
              agencies={config.agencies}
              emergencyTypes={config.emergency_types}
              loading={loading}
              activeCount={countActive(draft)}
            />
          </div>
        </>
      )}

      {/* ── Alerts list ─────────────────────────────────────────────── */}
      {!loading && listedAlerts.length === 0 && (
        <div className="border border-zinc-800 bg-[#0f1318] p-6 text-center space-y-2" data-testid="alerts-empty">
          <div className="text-sm text-zinc-300">Aucune alerte ne correspond aux critères sélectionnés.</div>
          {hasAnyFilter && (
            <button
              type="button"
              onClick={handleReset}
              data-testid="alerts-empty-reset"
              className="mt-1 border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      )}

      {listedAlerts.length > 0 && (
        <AdminSectionHeading icon={Siren} label="Journal d'alertes" count={listedAlerts.length} />
      )}
      {listedAlerts.map((a) => {
        const label = EMERGENCY_FR[a.emergency_type]?.label || a.emergency_type;
        const dotColor = EMERGENCY_META[a.emergency_type]?.color || "#64748B";
        const isUnacked = (unacked || []).some((x) => x.id === a.id);
        return (
          <div
            key={a.id}
            className={`border bg-[#0f1318] hover:border-zinc-600 transition-colors p-3 ${
              isUnacked
                ? "border-rose-500 cmd-unacked-pulse"
                : a.is_false
                  ? "border-rose-500/50"
                  : a.flagged
                    ? "border-amber-500/50"
                    : "border-zinc-800"
            }`}
            data-testid={`admin-alert-${a.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  aria-hidden
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ background: dotColor, boxShadow: `0 0 8px ${dotColor}` }}
                  data-testid={`admin-alert-${a.id}-type-dot`}
                />
                <div
                  className="font-display text-base font-black tracking-tight truncate"
                  style={{ color: dotColor }}
                >
                  {label}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {a.flagged && (
                  <span className="bg-amber-500/15 border border-amber-500/40 text-amber-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] flex items-center gap-1">
                    <Flag className="w-2.5 h-2.5" /> Flag
                  </span>
                )}
                {a.is_false && (
                  <span className="bg-rose-500/15 border border-rose-500/40 text-rose-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em]">
                    Fausse
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onLocate(a, dotColor, label)}
              data-testid={`admin-alert-${a.id}-locate`}
              title="Voir sur la carte"
              className="font-mono text-[11px] text-zinc-400 mt-1.5 inline-flex items-center gap-1 hover:text-emerald-300 focus:text-emerald-300 focus:outline-none transition-colors group tabular-nums"
            >
              <MapPin className="w-3 h-3 group-hover:scale-110 transition-transform" />
              <span className="underline decoration-dotted underline-offset-2">
                {a.coordinates.latitude.toFixed(4)}, {a.coordinates.longitude.toFixed(4)}
              </span>
              <span className="text-zinc-600"> · {fmt(a.created_at)}</span>
            </button>
            <div className="text-[11px] text-zinc-300 mt-1">
              <span className="text-zinc-500">Reporter : </span>
              <span className="font-mono font-bold text-zinc-100">
                {a.reporter_phone || "Anonyme"}
              </span>
            </div>
            {a.description && (
              <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{a.description}</p>
            )}
            {Array.isArray(a.attachments) && a.attachments.length > 0 && (
              <div
                className="flex gap-1.5 mt-2 overflow-x-auto cmd-scroll-row"
                data-testid={`admin-alert-attachments-${a.id}`}
              >
                {a.attachments.map((att, idx) => (
                  <a
                    key={`${att.url}-${idx}`}
                    href={mediaUrl(att.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 w-14 h-14 border border-zinc-700 bg-black overflow-hidden block"
                    title={att.name}
                  >
                    {att.kind === "image" ? (
                      <img src={mediaUrl(att.url)} alt={att.name || ""} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[9px] font-bold uppercase text-zinc-500">
                        {att.kind}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-2">
              <MedicalProfileView
                profile={a.medical_profile_snapshot}
                alertId={a.id}
                emergencyType={a.emergency_type}
                actor="admin"
                testid={`admin-alert-medical-${a.id}`}
              />
            </div>
            <div className={`grid gap-1.5 mt-3 ${isUnacked ? "grid-cols-3" : "grid-cols-2"}`}>
              {isUnacked && (
                <button
                  onClick={() => onAckAlert(a.id)}
                  data-testid={`admin-ack-${a.id}`}
                  className="border border-cyan-500/60 text-cyan-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-cyan-500 hover:text-black transition-colors flex items-center justify-center gap-1"
                >
                  <BellRing className="w-3 h-3" /> Acquitter
                </button>
              )}
              <button
                onClick={() => onMarkFalse(a.id, true)}
                data-testid={`admin-mark-false-${a.id}`}
                disabled={a.is_false}
                className="border border-rose-500/50 text-rose-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-rose-500 hover:text-white transition-colors disabled:opacity-40 flex items-center justify-center gap-1"
              >
                <XCircle className="w-3 h-3" /> Fausse
              </button>
              <button
                onClick={() => onMarkFalse(a.id, false)}
                data-testid={`admin-mark-true-${a.id}`}
                disabled={!a.is_false}
                className="border border-emerald-500/50 text-emerald-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-1"
              >
                <CheckCircle2 className="w-3 h-3" /> Légitime
              </button>
            </div>
            <AlertStatusControl alert={a} onStatusChanged={onStatusChanged} />
          </div>
        );
      })}
      {hasMore && (
        <button
          type="button"
          data-testid="admin-alerts-load-more"
          onClick={onLoadMore}
          disabled={loadingMoreEff}
          className="w-full border border-zinc-700 text-zinc-300 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] hover:border-emerald-500 hover:text-emerald-300 transition-colors disabled:opacity-60"
        >
          {loadingMoreEff ? "Chargement…" : "Charger plus"}
        </button>
      )}
    </div>
  );
};
